import { useEffect, useRef, type ReactNode } from 'react';

import { type Character as CharacterConfig } from '../../character/config';
import { HypeBoom } from '../HypeBoom';
import { SparkLayer, useSparks } from '../HypeMeter';
import { useStageEq, type EqSource } from './eq';
import { STAGE_CAPACITY, STAGE_FRONT_AT, StageScene } from './StageScene';

/**
 * The stage, with something on the screen.
 *
 * ## The screen is HTML over the SVG, not inside it
 *
 * The scene's screen is a rect at `(528, 96, 864, 486)` in a `0 0 1920 1080`
 * box, which is `27.5% / 8.889% / 45% / 45%` of it - and 864x486 is exactly
 * 16:9. So a plain absolutely-positioned div at those percentages sits on the
 * glass at every size, with no measuring and no resize observer.
 *
 * Everything then goes in it unchanged: an `img` for the artwork, `StreamPlayer`
 * for a broadcast, `TwitchPlayer` for a simulcast. A `foreignObject` would have
 * put the same elements inside the SVG and bought a compatibility problem -
 * video in `foreignObject` is the corner of SVG support that browsers disagree
 * about - for no benefit, because nothing about the screen needs to be part of
 * the drawing.
 *
 * ## Zoom, and the trap in it
 *
 * The scene and the screen are both 16:9, so "fill the frame with the screen"
 * lands exactly - no letterboxing, nothing to reconcile.
 *
 * The obvious implementation is a transform on a wrapper holding both, and it
 * is wrong: a `video` inside a CSS-scaled element rasterises at its layout size
 * and is then blown up, so a 480px stream stretched across 1060px is a soft
 * 480px stream. What actually happens is two animations on one timeline - the
 * art scales, and the screen box animates its own inset from those percentages
 * to `inset: 0`. The player is laid out at its final size the whole way and
 * stays sharp.
 *
 * `2.2222` and `50% 16.16%` are the scale and origin that map the screen rect
 * onto the frame. Horizontally the origin is dead centre because the screen is
 * centred; vertically it falls out of the arithmetic.
 *
 * ## Controlled, and the button is not here
 *
 * It was, in the corner of the frame, and it lost every fight with the track
 * controls drawn over the bottom of the venue - `z-index` cannot help, because
 * those are a later sibling of this whole element and no value inside a
 * stacking context escapes it.
 *
 * So the deck owns the state and the button, next to the other things somebody
 * presses, and this draws what it is told to. Which is the right split anyway:
 * the stage is the drawing, the deck is the chrome.
 */

/** How the screen is placed, closed and open. Both are percentages of the box. */
const SCREEN = {
  closed: { left: '27.5%', top: '8.889%', width: '45%', height: '45%' },
  open: { left: '0%', top: '0%', width: '100%', height: '100%' },
} as const;

/** Where the eye goes when the screen fills the frame. See the note above. */
const FOCUS = 'scale(2.2222)';
const FOCUS_ORIGIN = '50% 16.16%';

export function LiveStage({
  host,
  crowd,
  /** Whether the room is lively enough for the venue to move faster. */
  busy = false,
  /**
   * Whether the screen is worth zooming into.
   *
   * A stream is - it is the show. Artwork is not: filling the frame with a
   * still image is a worse picture than the room around it, so the control is
   * simply absent rather than present and pointless.
   */
  focused = false,
  /**
   * The record, for the bars on the screen to move to.
   *
   * Null falls back to the CSS loop, which is a screensaver and looks like one
   * - so it is the answer for "there is no track" and "there is no peaks file",
   * not for "this is a bit of work". See `eq.ts`.
   */
  eq = null,
  /**
   * The applause meter, if the viewer is in the room.
   *
   * Null for somebody who is only looking - the server refuses a tap from
   * outside the room, and a venue that threw sparks and moved no number would
   * be a lie told to the one person who cannot see the number move.
   */
  hype = null,
  /**
   * Whether what is on the screen is a picture rather than a player.
   *
   * Which decides where the tap surface goes. Artwork and the title card have
   * nothing to press, so the surface goes *over* them and the whole frame is
   * tappable - which is what anybody borrowing this gesture expects, because
   * the picture is the biggest thing on screen and the obvious thing to tap.
   *
   * A stream or a simulcast has its own controls, so the surface goes under it
   * instead and the venue around it stays tappable.
   */
  picture = false,
  /**
   * The season, which is a class on this element and four groups in the scene.
   *
   * Here rather than deeper because the recolours are descendant selectors -
   * `.stage-halloween #layer-truss circle` - so they need one ancestor carrying
   * the class, and this is the element every layer is inside.
   */
  theme = 'default',
  children,
}: {
  host: CharacterConfig;
  crowd: { config: CharacterConfig; name?: string }[];
  busy?: boolean;
  /** Whether the screen is filling the frame. Owned by the deck. */
  focused?: boolean;
  eq?: EqSource | null;
  hype?: { total: number; milestone: number | null; onTap: () => void } | null;
  theme?: string;
  picture?: boolean;
  /** What is on the screen. */
  children: ReactNode;
}) {
  /** Whether to animate between the two states, which the first paint must not. */
  const settled = useRef(false);

  useEffect(() => {
    settled.current = true;
  }, []);

  const zoomed = focused;

  /*
   * The bars, driven from here because the properties they read live on this
   * element - one write per bar per frame, rather than five hundred SVG nodes
   * through React.
   *
   * Nothing is driven while the screen fills the frame: the venue is entirely
   * behind an opaque player, so it would be a frame loop for something nobody
   * can see, on the one screen already paying for a video decode.
   */
  const root = useRef<HTMLDivElement | null>(null);
  const live = useStageEq(root, zoomed ? null : eq);

  /*
   * The venue itself is the button.
   *
   * Which is the whole gesture being borrowed here: you tap the thing you are
   * watching, wherever you happen to be looking, and it responds where your
   * finger landed. The meter in the controls row is the same tap for anybody
   * on a keyboard, and for anybody who would never think to press a drawing.
   */
  const { sparks, burst, done } = useSparks();

  /*
   * Off while a *player* fills the frame. At full zoom the thing under the
   * pointer is a video with its own controls, and a transparent layer over it
   * that swallowed a click to pause would be maddening in a way nobody would
   * be able to describe. A cover blown up is still only a picture, so that one
   * stays tappable.
   */
  const tappable = hype !== null && (!zoomed || picture);

  const tap = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!tappable) return;

    /*
     * Nothing gets selected on the way past.
     *
     * Tapping is a repeated press in one place, which is also how a browser is
     * asked to select something - so a fast tapper was left with the artwork
     * highlighted blue underneath their own sparks.
     */
    event.preventDefault();

    const bounds = event.currentTarget.getBoundingClientRect();

    burst(event.clientX - bounds.left, event.clientY - bounds.top);
    hype?.onTap();
  };

  /*
   * The tap surface, placed either side of the screen depending on what is on
   * it - see `picture`. Ordered rather than z-indexed, because the order *is*
   * the hit test: whichever of the two is later takes the press.
   */
  const surface = (
    <div
      data-stage-tap={tappable || undefined}
      onPointerDown={tap}
      aria-hidden
      className={`absolute inset-0 select-none ${tappable ? 'cursor-pointer' : 'pointer-events-none'}`}
    >
      <SparkLayer sparks={sparks} onDone={done} />
    </div>
  );
  const box = zoomed ? SCREEN.open : SCREEN.closed;
  const move = settled.current ? 'transition-[transform,inset,width,height] duration-500 ease-out' : '';

  return (
    <div
      ref={root}
      data-stage
      data-stage-focused={zoomed || undefined}
      data-stage-eq={live ? 'live' : undefined}
      /*
        No background of its own, and that matters.

        This element is exactly the 16:9 box, so a fill here is an opaque
        rectangle painted over the middle of the backdrop - which is what made
        the stretched sky look like it was not stretching at all. It was; this
        was sitting on it.
      */
      /*
        `select-none`, because a venue is a drawing and none of it is text
        anybody reads by dragging across it - and repeated pressing in one place
        is how a browser is asked to select something.
      */
      className={`group/stage relative w-full select-none overflow-hidden ${zoomed ? 'stage-focused' : ''} ${
        busy ? 'stage-hype' : ''
      } ${live ? 'stage-eq-live' : ''} ${
        theme === 'default' ? '' : `stage-${theme}`
      }`}
      style={{ aspectRatio: '16 / 9' }}
    >
      {/*
        Behind the screen: the truss, the lights and the bezel.
      */}
      <div
        // Nothing back here is a control, and the tap surface below has to be
        // able to receive a press that lands on a drawing of a speaker stack.
        className={`pointer-events-none absolute inset-0 ${move}`}
        style={{ transform: zoomed ? FOCUS : 'none', transformOrigin: FOCUS_ORIGIN }}
      >
        <StageScene layer="back" host={host} crowd={crowd} theme={theme} />
      </div>

      {/* Under the screen, when the screen is a player worth pressing. */}
      {!picture && surface}

      {/*
        The screen. Positioned, never scaled - see the note at the top.

        `bg-black` under it so the glass is black rather than the scene showing
        through while a stream is still connecting.
      */}
      <div
        data-stage-screen
        className={`absolute overflow-hidden bg-black ${zoomed ? '' : 'rounded-[12px]'} ${move}`}
        style={box}
      >
        {children}
      </div>

      {/* And over it, when it is a picture: a cover is the biggest thing on
          the screen and the obvious thing to tap. */}
      {picture && surface}

      {/*
        In front of it: the host, the booth, the crowd - painted and real.

        `pointer-events-none`, because this covers the screen and a click meant
        for a play button must not land on a drawing of a speaker stack.

        It fades out on the way in, and it has to. The DJ's head overlaps the
        bottom of the screen - that is the whole reason the scene is in halves -
        so at full zoom they are drawn two and a bit times life size across the
        middle of somebody's broadcast. Fading rather than unmounting so the way
        back is the same movement in reverse.
      */}
      <div
        className={`pointer-events-none absolute inset-0 ${move} ${
          zoomed ? 'opacity-0' : 'opacity-100'
        } transition-opacity duration-500`}
        style={{ transform: zoomed ? FOCUS : 'none', transformOrigin: FOCUS_ORIGIN }}
      >
        <StageScene layer="front" host={host} crowd={crowd} theme={theme} />

        {/*
          The painted crowd, three stage-widths wide and centred on this one.

          It is rows of blobs on a dark ground with no perspective and no fixed
          place on the stage, which makes it the one part of the scene that can
          be repeated sideways without looking repeated - so the floor and the
          heads carry out to the container's edges instead of stopping where the
          16:9 box does.

          `-left-full w-[300%]` rather than container units, because the tiles
          only have to agree with *this* box: a third of three widths is exactly
          one, whatever that turns out to be.
        */}
        <div className="absolute inset-y-0 -left-full flex w-[300%]" data-stage-audience>
          {[0, 1, 2].map((tile) => (
            <div key={tile} className="h-full w-1/3">
              <StageScene layer="audience" host={host} crowd={[]} theme={theme} />
            </div>
          ))}
        </div>

        {/* The real people, who stand in front of the painted ones. */}
        <div className="absolute inset-0">
          <StageScene layer="fans" host={host} crowd={crowd} theme={theme} />
        </div>
      </div>

      {/*
        A thousand, crossed while somebody was watching. Keyed on the number so
        two milestones in one broadcast are two animations rather than one that
        never restarts.
      */}
      {hype?.milestone != null && <HypeBoom key={hype.milestone} at={hype.milestone} />}
    </div>
  );
}

export { STAGE_CAPACITY, STAGE_FRONT_AT };
